import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { debounce } from '@theme/utilities';

/**
 * Search dialog pinned to the header `.search-action` bar.
 *
 * Native `<dialog>` lives in the top layer, so CSS cannot position it against
 * the trigger. Measure the visible search bar and expose `--search-anchor-*`
 * for the dialog styles.
 *
 * @extends DialogComponent
 */
class SearchModalComponent extends DialogComponent {
  /** @type {string[]} */
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#onClosed);
  }

  disconnectedCallback() {
    this.#unbindAnchorListeners();
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClosed);
    super.disconnectedCallback();
  }

  showDialog() {
    this.#syncAnchor();
    this.#bindAnchorListeners();
    super.showDialog();
    requestAnimationFrame(() => this.#syncAnchor());
  }

  /**
   * Desktop only. Mobile keeps the theme's full-screen search dialog.
   *
   * @returns {boolean}
   */
  #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  /**
   * Visible header search bar (skip duplicates that are `display: none`).
   *
   * @returns {HTMLElement | null}
   */
  #getSearchAction() {
    const actions = document.querySelectorAll('.search-action');

    for (const action of actions) {
      if (!(action instanceof HTMLElement)) continue;

      const rect = action.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return action;
    }

    return null;
  }

  #clearAnchor() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    dialog.classList.remove('search-modal__content--anchored');
    dialog.style.removeProperty('--search-anchor-top');
    dialog.style.removeProperty('--search-anchor-left');
    dialog.style.removeProperty('--search-anchor-width');
    dialog.style.removeProperty('--search-anchor-height');
  }

  #syncAnchor = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    if (!this.#isDesktop()) {
      this.#clearAnchor();
      return;
    }

    const action = this.#getSearchAction();
    if (!action) {
      this.#clearAnchor();
      return;
    }

    const rect = action.getBoundingClientRect();
    dialog.style.setProperty('--search-anchor-top', `${rect.top}px`);
    dialog.style.setProperty('--search-anchor-left', `${rect.left}px`);
    dialog.style.setProperty('--search-anchor-width', `${rect.width}px`);
    dialog.style.setProperty('--search-anchor-height', `${rect.height}px`);
    dialog.classList.add('search-modal__content--anchored');
  };

  #onResize = debounce(() => {
    if (this.refs.dialog?.open) this.#syncAnchor();
  }, 50);

  #bindAnchorListeners() {
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('scroll', this.#syncAnchor, { passive: true });
  }

  #unbindAnchorListeners() {
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('scroll', this.#syncAnchor);
  }

  #onClosed = () => {
    this.#unbindAnchorListeners();
  };
}

if (!customElements.get('search-modal-component')) {
  customElements.define('search-modal-component', SearchModalComponent);
}
